import React, { useEffect, useState } from "react"
import "./Login2.sass"
import { useLocation } from "react-router-dom"
import { ssoEmailAuthentication, ssoSiteSignIn, validateSsoSessionToken } from "../../hooks/api2"
import imageLogo from "../../static/logo-adigital-white.png"
import { FormattedMessage } from 'react-intl'

const Login2 = () => {
  const useQuery = () => {
    const { search } = useLocation()
    return React.useMemo(() => new URLSearchParams(search), [search])
  }

  const logout = () => {
    localStorage.removeItem("ssoToken")
    window.location.replace(window.location.protocol + "//" + window.location.host + "/error")
  }

  let query = useQuery()
  const domain = query.get("domain")

  const customizations = {
    'www.iamericas.ai': 'iamericas',
    'default': 'default'
  }
  const style = customizations[domain] || customizations.default

  let [mail, setMail] = useState("")
  let [loginDone, setLoginDone] = useState(false)
  let [sendingRequest, setSendingRequest] = useState(false)
  let [loginFail, setLoginFail] = useState(false)

  // SSO Token validation and installation
  useEffect(async () => {
    const ssoToken = query.get("ssoToken")

    if (ssoToken && ssoToken !== localStorage.getItem('ssoToken')) {
      if (!(await validateSsoSessionToken({ ssoToken })).ok) logout()
      else {
        localStorage.setItem("ssoToken", ssoToken)
        window.location.reload()
      }
    }
  })

  // Domain Token request and redirect
  useEffect(async () => {
    const redirect = query.get("redirect")
    const ssoToken = localStorage.getItem('ssoToken')

    if (ssoToken && domain) {
      setSendingRequest(true)
      const siteTokenResponse = await ssoSiteSignIn(ssoToken, domain)

      if (!siteTokenResponse.ok) logout()
      else {
        const siteToken = (await siteTokenResponse.json()).token

        const redirectUrl = (new URL(decodeURIComponent(redirect || `https://${domain}`)))
        redirectUrl.searchParams.append('ds_session', siteToken)
        window.location.replace(redirectUrl.toString())
      }
    }
  })

  useEffect(async () => {
    const domain = query.get("domain")

    if (!domain && localStorage.getItem('ssoToken')) {
      window.location.replace(window.location.protocol + "//" + window.location.host + "/userPanel")
    }
  })


  const loginFailRender = () =>
    <div className="form-content">
      <h2><FormattedMessage id={`login2.${style}.fail.usernotfound`} /></h2>
      <p>
        <FormattedMessage id={`login2.${style}.fail.message`} values={
          {
            link: <a href="" onClick={() => window.location.reload()}>
              <FormattedMessage id={`login2.${style}.fail.message.link`} />
            </a>
          }
        } />
      </p>
      <p>
        <FormattedMessage id={`login2.${style}.fail.fallback`} values={
          {
            mailto: <FormattedMessage id={`login2.${style}.fail.fallback.mailto`}>
              {
                (mailtoaddress) => <a href={`mailto:${mailtoaddress}`}>
                  {mailtoaddress}
                </a>
              }
            </FormattedMessage>
          }
        } />
      </p>
    </div>

  const loginFormRender = () =>
    <form
      className="form"
      onSubmit={async (e) => {
        setSendingRequest(true)
        e.preventDefault()
        const res = await ssoEmailAuthentication(
          mail.trim().toLowerCase(),
          {
            domain: query.get("domain"),
            redirect: query.get("redirect")
          }
        )
        if (res.status === 200) {
          setSendingRequest(false)
          setLoginDone(true)
        } else {
          if (res.status === 404) {
            setLoginFail(true)
          }
        }
        e.target.reset()
      }}
    >
      <div className="form-content">
        <h2><FormattedMessage id={`login2.${style}.form.title`} /></h2>
        <p><FormattedMessage id={`login2.${style}.form.message`} /></p>
        <FormattedMessage id={`login2.${style}.form.email.placeholder`}>
          {
            (ph) => <input
              value={mail}
              onChange={(e) => setMail(e.target.value)}
              placeholder={ph}
              type="text"
            />
          }
        </FormattedMessage>
        <button
          disabled={mail === "" || sendingRequest === true}
          className={`${mail === "" || sendingRequest === true ? "empty-button" : "ready-button"} ${style}`}
          type="submit"
        >
          {sendingRequest ?
            <FormattedMessage id={`login2.${style}.form.submitting`} /> :
            <FormattedMessage id={`login2.${style}.form.submit`} />
          }
        </button>
        <p className="even-smaller-paragraph">
          <FormattedMessage id={`login2.${style}.legal`} values={
            {
              privacyLink: <FormattedMessage id={`login2.${style}.legal.privacyLink`}>
                {
                  (link) => <a href={link}>
                    <FormattedMessage id={`login2.${style}.legal.privacyLink.text`} />
                  </a>
                }
              </FormattedMessage>,
              tosLink: <FormattedMessage id={`login2.${style}.legal.tosLink`}>
                {
                  (link) => <a href={link}>
                    <FormattedMessage id={`login2.${style}.legal.tosLink.text`} />
                  </a>
                }
              </FormattedMessage>
            }
          } />
        </p>
      </div>
    </form>

  const loginDoneRender = () =>
    <div className="form-content">
      <h2><FormattedMessage id={`login2.${style}.success.title`} /></h2>
      <p><FormattedMessage id={`login2.${style}.success.message`} /></p>

      <p className="smaller-paragraph">
        <FormattedMessage id={`login2.${style}.success.info`} values={
          {
            reload: <a href="" onClick={() => window.location.reload()}>
              <FormattedMessage id={`login2.${style}.success.info.reload`} />
            </a>,
            mailto: <FormattedMessage id={`login2.${style}.success.info.mailto`}>
              {
                (mailtoaddress) => <a href={`mailto:${mailtoaddress}`}>
                  {mailtoaddress}
                </a>
              }
            </FormattedMessage>
          }
        } />
      </p>
    </div>

  // Not installing SSO token & not auto-signing-in, so lets show the login screen
  return (
    <div id="login2" className={style}>
      {style === 'default' && //TODO move to CSS
        <div className="logo">
          <img src={imageLogo} />
        </div>
      }
      <div className="login-content">
        <div className="form-login">
          {loginFail && loginFailRender()}
          {!loginFail && !loginDone && loginFormRender()}
          {!loginFail && loginDone && loginDoneRender()}
        </div>
        <div className={`login-image ${style}`} />
      </div>
    </div>
  )
}

export default Login2